/**
 * Calculate brightness of a color (0-255)
 * Uses perceived brightness formula
 */
export const getColorBrightness = (color: string): number => {
  const raw = color.trim()
  if (!raw) {
    return 128
  }

  if (raw.startsWith('#')) {
    let hex = raw.slice(1)
    if (hex.length === 3) {
      hex = hex
        .split('')
        .map((c) => c + c)
        .join('')
    }
    if (hex.length !== 6) {
      return 128
    }
    const r = parseInt(hex.slice(0, 2), 16)
    const g = parseInt(hex.slice(2, 4), 16)
    const b = parseInt(hex.slice(4, 6), 16)
    if ([r, g, b].some((n) => Number.isNaN(n))) {
      return 128
    }
    return (r * 299 + g * 587 + b * 114) / 1000
  }

  const rgbMatch = raw.match(/^rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i)
  if (rgbMatch) {
    const r = parseInt(rgbMatch[1], 10)
    const g = parseInt(rgbMatch[2], 10)
    const b = parseInt(rgbMatch[3], 10)
    if ([r, g, b].some((n) => Number.isNaN(n))) {
      return 128
    }
    return (r * 299 + g * 587 + b * 114) / 1000
  }

  return 128
}

/**
 * Get average brightness from an image URL
 * Samples the image and calculates average brightness.
 * Returns null if the image cannot be sampled (CORS, decode error, etc.)
 */
export const getImageBrightness = async (imageUrl: string): Promise<number | null> => {
  return new Promise((resolve) => {
    const img = new Image()
    img.crossOrigin = 'anonymous'

    img.onload = (): void => {
      const canvas = document.createElement('canvas')
      const ctx = canvas.getContext('2d')

      if (!ctx) {
        resolve(null)
        return
      }

      canvas.width = 100
      canvas.height = 100

      try {
        ctx.drawImage(img, 0, 0, 100, 100)
      } catch {
        resolve(null)
        return
      }

      try {
        const imageData = ctx.getImageData(0, 0, 100, 100)
        const data = imageData.data

        let totalBrightness = 0
        for (let i = 0; i < data.length; i += 4) {
          const r = data[i]
          const g = data[i + 1]
          const b = data[i + 2]
          totalBrightness += (r * 299 + g * 587 + b * 114) / 1000
        }

        const avgBrightness = totalBrightness / (data.length / 4)
        resolve(avgBrightness)
      } catch (error) {
        console.warn('Could not analyze image brightness:', error)
        resolve(null)
      }
    }

    img.onerror = (): void => {
      resolve(null)
    }

    img.src = imageUrl
  })
}

/** Matches default in BackgroundContainer when theme.backgroundColor is unset */
const DEFAULT_SOLID_BACKGROUND = '#F8FAFC'

/**
 * Auto-detect text color based on background
 * Returns white for dark backgrounds, black for light backgrounds
 */
export const getAutoTextColor = async (
  backgroundColor?: string,
  backgroundImageUrl?: string
): Promise<string> => {
  const solid = backgroundColor?.trim() || DEFAULT_SOLID_BACKGROUND
  let brightness = 128

  if (backgroundImageUrl) {
    const fromImage = await getImageBrightness(backgroundImageUrl)
    if (fromImage !== null) {
      brightness = fromImage
    } else {
      brightness = getColorBrightness(solid)
    }
  } else {
    brightness = getColorBrightness(solid)
  }

  // Threshold: 128 (medium brightness)
  // Dark background (< 128) -> white text
  // Light background (>= 128) -> black text
  return brightness < 128 ? '#FFFFFF' : '#000000'
}
